import Image from 'next/image';
import PropTypes from 'prop-types';
import {useMemo} from 'react';
import ReactCountryFlag from 'react-country-flag';

import {ROLES_NAMES} from '@/src/app/constants/roles';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import {Button, Separator, Text} from '../../../ui';
import {STUDENT_ID_STATUSES} from '../constants/studentIdStatuses';
import css from '../styles/PersonalDetailsWidgetLayout.module.css';

const PersonalDetailsWidgetLayout = ({
  firstName,
  lastName,
  email,
  onEditFullNameClick,
  userType,
  university,
  studentIdStatus,
  onReuploadStudentIdClick,
  studentIdNumber,
  country,
}) => {
  const studentIdStatusNode = useMemo(() => {
    switch (studentIdStatus) {
      case STUDENT_ID_STATUSES.EMPTY: {
        return (
          <Button
            color={Button.COLOR.YELLOW}
            onClick={onReuploadStudentIdClick}
            height={Button.HEIGHT.SMALL}
          >
            <TextLocal id={'job-portal.upload'} />
          </Button>
        );
      }
      case STUDENT_ID_STATUSES.CANCELED: {
        return (
          <Button
            color={Button.COLOR.YELLOW}
            onClick={onReuploadStudentIdClick}
            height={Button.HEIGHT.SMALL}
          >
            <TextLocal id={'profile.reupload-student-id'} />
          </Button>
        );
      }
      case STUDENT_ID_STATUSES.ACCEPTED: {
        return (
          <Text
            color={Text.COLOR.GRADIENT}
            fontWeight={Text.FONT_WEIGHT.BOLD}
            fontSize={Text.FONT_SIZE.SIZE_16}
          >
            <TextLocal id={'profile.document-accepted'} />
          </Text>
        );
      }
      case STUDENT_ID_STATUSES.PENDING: {
        return (
          <Text
            color={Text.COLOR.ORANGE}
            fontSize={Text.FONT_SIZE.SIZE_16}
            fontWeight={Text.FONT_WEIGHT.BOLD}
          >
            <TextLocal id={'profile.pending-student-id'} />
          </Text>
        );
      }
      default: {
        return null;
      }
    }
  }, [studentIdStatus, onReuploadStudentIdClick]);

  const studentIdDescriptionNode = useMemo(() => {
    switch (studentIdStatus) {
      case STUDENT_ID_STATUSES.CANCELED: {
        return <TextLocal id={'profile.your-student-id-has-been-canceled'} />;
      }
      case STUDENT_ID_STATUSES.ACCEPTED: {
        return null;
      }
      case STUDENT_ID_STATUSES.PENDING: {
        return <TextLocal id={'profile.your-student-id-is-on-verification'} />;
      }
      default: {
        return null;
      }
    }
  }, [studentIdStatus, onReuploadStudentIdClick]);

  return (
    <div className={css.widget}>
      <div className={css.headingBlock}>
        <Text
          fontSize={Text.FONT_SIZE.SIZE_32}
          fontWeight={Text.FONT_WEIGHT.MEDIUM}
          fontFamily={Text.FONT_FAMILY.RUBIK}
        >
          <TextLocal id={'profile.personal-details'} />
        </Text>
        <div className={css.editFullNameButton} onClick={onEditFullNameClick}>
          <Image
            src={`/icons/editIcon.svg`}
            loading="lazy"
            alt="true"
            width={14}
            height={14}
          />
        </div>
      </div>
      <div className={css.userFullNameBlock}>
        <div className={css.fullName}>
          <Text
            fontSize={Text.FONT_SIZE.SIZE_18}
            fontWeight={Text.FONT_WEIGHT.BOLD}
          >
            {`${firstName} ${lastName}`}
          </Text>
          {Boolean(country) && (
            <div className={css.country}>
              <ReactCountryFlag countryCode={country} />
            </div>
          )}
        </div>
        <Text
          color={Text.COLOR.GREY}
          fontSize={Text.FONT_SIZE.SIZE_16}
          isOneLine={true}
          lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_24}
        >
          {email}
        </Text>
      </div>
      {userType === ROLES_NAMES.STUDENT && (
        <div className={css.studentIdWidget}>
          <div className={css.studentIdRow}>
            <div className={css.studentIdDescription}>
              <div className={css.studentIdNumber}>
                <Text
                  fontSize={Text.FONT_SIZE.SIZE_16}
                  fontWeight={Text.FONT_WEIGHT.BOLD}
                >
                  <TextLocal
                    id={'profile.student-id-with-number'}
                    params={{studentId: studentIdNumber || ''}}
                  />
                </Text>
              </div>
              {university && (
                <div className={css.studentIdUniversity}>
                  <Text
                    color={Text.COLOR.GREY}
                    fontSize={Text.FONT_SIZE.SIZE_16}
                  >
                    {university}
                  </Text>
                </div>
              )}
            </div>
            <div className={css.buttonWrapper}>{studentIdStatusNode}</div>
          </div>
          <div className={css.studentIdSecondRow}>
            <Text color={Text.COLOR.GREY} fontSize={Text.FONT_SIZE.SIZE_14}>
              {studentIdDescriptionNode}
            </Text>
          </div>
        </div>
      )}
      <Separator borderWeight={Separator.BORDER_WEIGHT.THIN} />
    </div>
  );
};

PersonalDetailsWidgetLayout.propTypes = {
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  onEditFullNameClick: PropTypes.func.isRequired,
  studentIdNumber: PropTypes.string,
  university: PropTypes.string,
  studentIdStatus: PropTypes.oneOf(Object.values(STUDENT_ID_STATUSES)),
  onReuploadStudentIdClick: PropTypes.func.isRequired,
  userType: PropTypes.oneOf(Object.values(ROLES_NAMES)),
  country: PropTypes.string,
};

export default PersonalDetailsWidgetLayout;
